import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from './AuthContext';

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan_type?: string;
  tokens_purchased?: number;
  tokens_used?: number;
  tokens_remaining?: number;
  subscription_status?: string;
  member_count?: number;
}

interface OrgContextType {
  activeOrg: Organization | null;
  organizations: Organization[];
  setActiveOrg: (org: Organization) => void;
  switchOrg: (orgId: string) => void;
  refreshOrgs: () => Promise<void>;
  isLoading: boolean;
  /** Header to include in all API calls */
  orgHeaders: Record<string, string>;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

export const OrgProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [activeOrg, setActiveOrgState] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const role = (user?.user_role || '').toLowerCase();
  const roleName = (user?.role_name || '').toLowerCase();
  const isSuperAdmin = role === 'super_admin' || roleName === 'super_admin' || roleName === 'super admin';

  // Load organizations when user is authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      loadOrgs();
    } else {
      setActiveOrgState(null);
      setOrganizations([]);
    }
  }, [isAuthenticated, token]);

  const loadOrgs = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      if (isSuperAdmin) {
        // Super admin sees all orgs
        const res = await fetch(`${API_BASE_URL}/organizations/all`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        if (data.success && data.data) {
          setOrganizations(data.data);
          // Match org to the schema user logged into, then fallback to saved, then first
          const loginSchema = (user as any)?.active_schema;
          const schemaOrg = loginSchema ? data.data.find((o: any) => o.schema_name === loginSchema) : null;
          const savedOrgId = localStorage.getItem('active_org_id');
          const savedOrg = data.data.find((o: Organization) => o.id === savedOrgId);
          const selected = schemaOrg || savedOrg || data.data[0] || null;
          setActiveOrgState(selected);
          if (selected) localStorage.setItem('active_org_id', selected.id);
        }
      } else {
        // Regular user — get their own org
        const res = await fetch(`${API_BASE_URL}/organizations/my-org`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        if (data.success && data.data) {
          const org = data.data;
          setOrganizations([org]);
          setActiveOrgState(org);
        }
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setActiveOrg = (org: Organization) => {
    setActiveOrgState(org);
    localStorage.setItem('active_org_id', org.id);
  };

  const switchOrg = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) setActiveOrg(org);
  };

  const refreshOrgs = async () => {
    await loadOrgs();
  };

  // Header to include in all API calls — backend reads X-Organization-Id
  const orgHeaders: Record<string, string> = activeOrg
    ? { 'X-Organization-Id': activeOrg.id }
    : {};

  return (
    <OrgContext.Provider value={{ activeOrg, organizations, setActiveOrg, switchOrg, refreshOrgs, isLoading, orgHeaders }}>
      {children}
    </OrgContext.Provider>
  );
};
